import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AthletesModel,
  CurrentCategoryWidgetData,
  SyncMeta,
  ThemeColors,
} from "../../../util/src";
import WebsocketClient from "../../../util/src/websocket-client";
import CurrentCategoryRankingWidgetChallengeHiver2025 from "./ChallengeHiver2025/CurrentCategoryWidget";

interface CurrentCategoryRankingWidgetProps {
  meetId: string;
  token?: string;
  serverUrl?: string;
  colors: ThemeColors;
  theme?: string;
  durations?: { exit: number; hidden: number; show: number };
}

const MotionBox = motion.create(Box);

const CurrentCategoryRankingWidget: React.FC<
  CurrentCategoryRankingWidgetProps
> = ({ meetId, token, serverUrl, colors, theme, durations }) => {
  const [currentAthlete, setCurrentAthlete] = useState<AthletesModel | null>(
    null,
  );
  const durationsCurrentCategory = useMemo(
    () =>
      durations ?? {
        exit: 5,
        hidden: 30,
        show: 10,
      },
    [durations],
  );
  const [categoryAthletes, setCategoryAthletes] = useState<AthletesModel[]>([]);
  const [placementIfSuccessful, setPlacementIfSuccessful] = useState<
    number | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const refreshRef = useRef<boolean>(true);
  const syncMetaRef = useRef<SyncMeta | null>(null);

  const currentAthleteRef = useRef<AthletesModel | null>(null);

  const timeoutsRef = useRef<number[]>([]);
  const hideShowTimeoutsRef = useRef<number[]>([]);

  const addTimeout = (
    ref: React.MutableRefObject<number[]>,
    fn: () => void,
    ms: number,
  ) => {
    const id = window.setTimeout(() => {
      ref.current = ref.current.filter((t) => t !== id);
      fn();
    }, ms);
    ref.current.push(id);
    return id;
  };

  const clearTimeouts = (ref: React.MutableRefObject<number[]>) => {
    for (const id of ref.current) window.clearTimeout(id);
    ref.current = [];
  };

  useEffect(() => {
    currentAthleteRef.current = currentAthlete;
  }, [currentAthlete]);

  useEffect(() => {
    syncMetaRef.current = null;
  }, [meetId, token, serverUrl]);

  useEffect(() => {
    const onCurrentCategory = (payload: unknown) => {
      const data = payload as {
        payload: CurrentCategoryWidgetData | undefined;
        syncMeta: SyncMeta;
      };
      const last = syncMetaRef.current;
      const incoming = data.syncMeta;

      if (!last || incoming.sessionId !== last.sessionId) {
        syncMetaRef.current = incoming;
      } else if (incoming.rev <= last.rev) {
        return;
      } else {
        syncMetaRef.current = incoming;
      }
      if (!data.payload) {
        addTimeout(
          timeoutsRef,
          () => {
            setIsTransitioning(true);
            addTimeout(
              timeoutsRef,
              () => {
                setCategoryAthletes([]);
                setCurrentAthlete(null);
                setPlacementIfSuccessful(null);
                setIsTransitioning(false);
              },
              500,
            );
          },
          durationsCurrentCategory.exit * 1000,
        );
        return;
      }
      const { athlete, sorted, placementIfSuccessful } = data.payload;
      if (!athlete) {
        setCategoryAthletes([]);
        setCurrentAthlete(null);
        setPlacementIfSuccessful(null);
        return;
      }

      // Vérifier si la catégorie a changé
      const categoryChanged =
        currentAthleteRef.current == null ||
        currentAthleteRef.current.catPoidId !== athlete.catPoidId ||
        currentAthleteRef.current.catAgeId !== athlete.catAgeId ||
        currentAthleteRef.current.sexe !== athlete.sexe;

      if (
        currentAthleteRef.current != null &&
        currentAthleteRef.current.id !== athlete.id
      ) {
        setIsHidden(false);
      }
      clearTimeouts(hideShowTimeoutsRef);
      if (categoryChanged) {
        setIsHidden(true);
        addTimeout(
          timeoutsRef,
          () => {
            refreshRef.current = false;
            setIsHidden(false);
            setIsTransitioning(true);
            addTimeout(
              timeoutsRef,
              () => {
                setCurrentAthlete(athlete);
                setCategoryAthletes(sorted);
                setPlacementIfSuccessful(placementIfSuccessful);
                addTimeout(timeoutsRef, () => setIsTransitioning(false), 500);
                addTimeout(
                  hideShowTimeoutsRef,
                  () => {
                    if (!isHidden) setIsHidden(true);
                    addTimeout(
                      hideShowTimeoutsRef,
                      () => {
                        setIsHidden(false);
                      },
                      durationsCurrentCategory.hidden * 1000,
                    );
                  },
                  durationsCurrentCategory.show * 1000,
                );
              },
              500,
            );
          },
          refreshRef.current ? 0 : durationsCurrentCategory.exit * 1000,
        );
      } else {
        // Mise à jour sans transition si seul l'athlète change
        addTimeout(
          timeoutsRef,
          () => {
            setCurrentAthlete(athlete);
            refreshRef.current = false;
          },
          refreshRef.current ? 0 : durationsCurrentCategory.exit * 1000,
        );
        setCategoryAthletes(sorted);
        setPlacementIfSuccessful(placementIfSuccessful);
        addTimeout(
          hideShowTimeoutsRef,
          () => {
            if (!isHidden) setIsHidden(true);
            addTimeout(
              hideShowTimeoutsRef,
              () => {
                setIsHidden(false);
              },
              durationsCurrentCategory.hidden * 1000,
            );
          },
          durationsCurrentCategory.show * 1000 +
            durationsCurrentCategory.exit * 1000,
        );
      }
    };

    const onceConnected = (wsClient: WebsocketClient) => {
      if (wsClient.isConnected()) {
        wsClient.off(`${meetId}/current-category`, onCurrentCategory);
        wsClient.on(`${meetId}/current-category`, onCurrentCategory);
        wsClient.emit("current-category", { id: meetId, auth_token: token });
      }
    };
    if (serverUrl) {
      const wsClient = WebsocketClient.getInstance(serverUrl);
      if (!wsClient.isConnected()) {
        wsClient.connect(() => onceConnected(wsClient));
      } else {
        onceConnected(wsClient);
      }
      return () => {
        wsClient.off(`${meetId}/current-category`, onCurrentCategory);
      };
    }
  }, [
    meetId,
    token,
    serverUrl,
    durationsCurrentCategory.exit,
    durationsCurrentCategory.show,
    durationsCurrentCategory.hidden,
    isHidden,
  ]);

  useEffect(() => {
    if (error) setError(null);
  }, [error]);

  if (!currentAthlete || categoryAthletes.length === 0 || error) {
    return null;
  }

  if (
    currentAthlete &&
    categoryAthletes.length > 0 &&
    theme === "Challenge d'Hiver 2025"
  ) {
    return (
      <CurrentCategoryRankingWidgetChallengeHiver2025
        isVisible={!isHidden}
        placementIfSuccessful={placementIfSuccessful}
        currentAthlete={currentAthlete}
        categoryAthletes={categoryAthletes}
      />
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "all 0.5s ease-in-out",
          transform:
            isTransitioning || isHidden ? "translateX(100%)" : "translateX(0)",
          alignSelf: "stretch",
        }}
      >
        {/* En-tête avec la catégorie */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "rgba(0, 0, 0, 0.98)",
            color: "white",
            textAlign: "center",
            transform: "skewX(-10deg)",
            margin: "0px 8px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontSize: "1.5rem",
              fontWeight: "bold",
              transform: "skewX(10deg)",
              transition: "font-size 0.5s ease-in-out",
            }}
          >
            {currentAthlete.catPoid?.nom} {currentAthlete.catAge?.code}
          </Typography>
        </Box>

        {/* Liste des athlètes */}
        <MotionBox
          layout
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 1,
            pt: 0.5,
            overflowY: "auto",
          }}
        >
          {categoryAthletes.map((athlete) => {
            return (
              <MotionBox
                key={athlete.id}
                layout
                initial={false}
                transition={{
                  type: "spring",
                  stiffness: 80,
                  damping: 30,
                }}
                sx={{
                  position: "relative",
                  zIndex: athlete.id === currentAthlete?.id ? 1000 : 0,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    transform: "skewX(-10deg)",
                    transition: "all 0.5s ease-in-out",
                    zIndex: athlete.id === currentAthlete?.id ? 1000 : 0,
                    backgroundColor:
                      athlete.id === currentAthlete?.id
                        ? colors.background
                        : colors.primary,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      transform: "skewX(10deg)",
                      width: 40,
                      fontWeight: "bold",
                      color:
                        athlete.id === currentAthlete.id
                          ? colors.backgroundText
                          : colors.primaryText,
                      fontSize: "1rem",
                      transition: "font-size 0.5s ease-in-out",
                    }}
                  >
                    {athlete.isDisqualified ? "HM" : athlete.placement}.
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      transform: "skewX(10deg)",
                      flex: 1,
                      color:
                        athlete.id === currentAthlete.id
                          ? colors.backgroundText
                          : colors.primaryText,
                      fontSize: "1rem",
                      transition: "font-size 0.5s ease-in-out",
                    }}
                  >
                    {athlete.prenom} {athlete.nom}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      transform: "skewX(10deg)",
                      width: 100,
                      textAlign: "right",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      color:
                        athlete.id === currentAthlete.id
                          ? colors.backgroundText
                          : colors.primaryText,
                      transition: "font-size 0.5s ease-in-out",
                    }}
                  >
                    {athlete.totalkg || "-"}
                  </Typography>
                </Box>
              </MotionBox>
            );
          })}
        </MotionBox>
      </Box>
    </Box>
  );
};

import PropTypes from "prop-types";

CurrentCategoryRankingWidget.propTypes = {
  meetId: PropTypes.string.isRequired,
  serverUrl: PropTypes.string,
  token: PropTypes.string,
  colors: PropTypes.any.isRequired,
  theme: PropTypes.string,
  durations: PropTypes.any,
};

export default CurrentCategoryRankingWidget;
